import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, PhoneCall } from "lucide-react";
import { api } from "@/lib/api";
import { V } from "@/lib/vocab";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// +15551234567 → +1 (555) 123-4567. Short codes and WhatsApp handles pass through untouched.
export const prettyNumber = (n) => {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(n || "");
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : n || "—";
};

function NumberRow({ r, testid }) {
  const bits = [
    r.texts_in ? `${r.texts_in} text${r.texts_in === 1 ? "" : "s"} in` : null,
    r.texts_out ? `${r.texts_out} out` : null,
    r.calls ? `${r.calls} call${r.calls === 1 ? "" : "s"}` : null,
    r.voice_minutes ? `${r.voice_minutes} voice min` : null,
    r.ai_answers ? `${r.ai_answers} AI answer${r.ai_answers === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  return (
    <div data-testid={testid} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-sand-50 py-2.5 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-heading text-[13px] font-bold tabular-nums text-ink">{prettyNumber(r.number)}</span>
          {r.label && <span className="truncate text-xs text-ink/55">{r.label}</span>}
        </div>
        <div className="mt-0.5 text-[11px] text-ink/55" title={`${r.segments_in} segments in · ${r.segments_out} segments out`}>
          {bits.length ? bits.join(" · ") : "No billable activity"}
        </div>
      </div>
      <div className="font-heading text-sm font-extrabold tabular-nums text-ink">{money(r.cost)}</div>
    </div>
  );
}

export function UsageByNumber({ tenantId, month }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/tenants/${tenantId}/usage/by-number${month ? `?month=${month}` : ""}`)
      .then((r) => { if (live) setD(r.data); })
      .catch(() => { if (live) setD(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [tenantId, month]);

  const csv = async () => {
    try {
      const r = await api.get(`/tenants/${tenantId}/usage/by-number.csv?month=${d.month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `usage-by-number-${d.month}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't export the CSV"); }
  };

  if (loading) return <div className="flex justify-center py-8 text-ink/40" data-testid="usage-by-number-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!d) return null;
  const un = d.unattributed || {};
  const unTotal = (un.texts || 0) + (un.calls || 0) + (un.ai_answers || 0);
  const empty = !d.lines?.length && !d.callers?.length;

  return (
    <div className="rounded-2xl border border-sand-200 bg-white p-5 md:px-6" data-testid="usage-by-number">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-redline-50"><PhoneCall className="h-[18px] w-[18px] text-redline-500" strokeWidth={1.5} /></div>
          <div className="min-w-0">
            <h3 className="font-heading text-[15px] font-bold text-ink">By phone number</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">Texts, calls and AI answers for {d.month}, split by the {V.lc("org")} line they ran on and by each caller.</p>
          </div>
        </div>
        {!empty && (
          <button data-testid="usage-by-number-csv" onClick={csv}
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-sand-50">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        )}
      </div>

      {empty ? (
        <p className="mt-4 rounded-xl bg-sand-50 px-4 py-3 text-sm text-ink/60" data-testid="usage-by-number-empty">
          No texts or calls metered against a phone number in {d.month} yet.
        </p>
      ) : (
        <>
          {!!d.lines?.length && (
            <div className="mt-4" data-testid="usage-by-number-lines">
              <div className="text-xs font-bold uppercase tracking-wide text-ink/45">Your {V.lc("org")} lines</div>
              <div className="mt-1">{d.lines.map((r) => <NumberRow key={r.number} r={r} testid={`usage-line-number-${r.number}`} />)}</div>
            </div>
          )}
          {!!d.callers?.length && (
            <div className="mt-5" data-testid="usage-by-number-callers">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-xs font-bold uppercase tracking-wide text-ink/45">Top callers</div>
                {d.callers_total > d.callers.length && <div className="text-[11px] text-ink/45">Showing {d.callers.length} of {d.callers_total} — the CSV has them all</div>}
              </div>
              <div className="mt-1">{d.callers.map((r) => <NumberRow key={r.number} r={r} testid={`usage-caller-number-${r.number}`} />)}</div>
            </div>
          )}
        </>
      )}

      {unTotal > 0 && (
        <p className="mt-4 text-xs text-ink/50" data-testid="usage-by-number-unattributed">
          {unTotal} item{unTotal === 1 ? "" : "s"} aren&rsquo;t tied to a number ({money(un.cost)}) — mostly activity metered before per-number tracking, plus AI answers with no caller.
        </p>
      )}
    </div>
  );
}
